import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { TheWestSection } from '@news-mono/common'
import {
    AppProps,
    AuthenticationHandler,
    PageResolver,
    ProvideRouteResolvedDataToTheme,
    regionalCanonicalMeta,
} from '@news-mono/component-library'
import {
    errorLocation,
    Features,
    getRedirectRouteInfo,
    getSectionMetaInfo,
    matchedErrorRoutePages,
    Product,
    registerPageRoutes,
    RenderTargetContext,
    StaticRoutes,
} from '@news-mono/web-common'
import { isArticleLikePublication } from '@west-australian-newspapers/publication-types'
import { PageProps } from '@west-australian-newspapers/page-lifecycle-provider'
import queryString from 'query-string'
import React from 'react'
import { useLocation } from 'react-router'
import './App.css'
import { layout } from './App.routing'
import { staticRoutes } from './routes'
import { createAflMatchCentreRoute } from './routes/afl-match-centre-routes/get-afl-match-centre-route'
import { createCookieSettingsPage } from './routes/cookie-consent'
import { createDiagPage } from './routes/diag-routes'
import {
    matchedSomethingWentWrongRoute,
    notFoundRoute,
    staticSomethingWentWrongRoute,
} from './routes/errors'
import { getProfilePageRouteInfo } from './routes/profile/get-profile-page-route-info'
import { createArticleRoute } from './routes/publication/kind/article'
import { createFeatureRoute } from './routes/publication/kind/feature'
import { createGalleryRoute } from './routes/publication/kind/gallery'
import { createShorthandRoute } from './routes/publication/kind/shorthand'
import { getSearchPageRouteInfo } from './routes/search/get-search-page-route-info'
import { getTopicPageRouteInfo } from './routes/topic/get-topic-page-route-info'
import { Site, siteAds } from './Site'
import { datadogRum } from '@datadog/browser-rum'
import { getLoadedElectionDefinition } from './routes/elections/routes'
import {
    createSpecificElectionEmbedPage,
    createSpecificElectionLandingPage,
    createSpecificElectionMapPage,
    createSpecificElectoratesPage,
    createSpecificPartyTotalsPage,
    createSpecificPollieRaterPage,
} from './routes/elections/pages/createRoutes'

export function initRouting() {
    registerPageRoutes<TheWestSection>({
        staticRoutes,
        matchRoutes: {
            '/newsletter-redirect': () => {
                return {
                    kind: 'redirect',
                    redirectTo: {
                        targetUrl:
                            'https://emails.thewest.com.au/subscribe/index.html',
                    },
                }
            },
            // TODO Cleanup the double function call
            '/search$': (services) =>
                getSearchPageRouteInfo(
                    queryString.parse(services.location.search),
                )(services),
            '/diag$': ({ config, resolution }) => {
                const section =
                    resolution.type === 'match' && resolution.section
                        ? resolution.section
                        : 'default'
                return createDiagPage(config, section as TheWestSection)
            },
            '/cookie-settings$': ({ resolution, store }) => {
                const section =
                    resolution.type === 'match' && resolution.section
                        ? resolution.section
                        : 'default'

                return createCookieSettingsPage(
                    store,
                    section as TheWestSection,
                )
            },
            // The following are required for regional error page matching if you want to explicitly view a regional something went wrong page
            [`${errorLocation}$`]: ({ config, getAdTargeting, resolution }) =>
                matchedSomethingWentWrongRoute(
                    config,
                    getAdTargeting,
                    resolution,
                ),
            ...(matchedErrorRoutePages as StaticRoutes<TheWestSection>),
        },
        serverRoutes: {
            elections: ({ resolution, getAdTargeting, store, config }) => {
                const electionId = resolution.resolution.electionId

                /**
                 * Hard coded checks to remove -election-2025 from id
                 * Part of https://wanews.atlassian.net/browse/EDIT-2006?atlOrigin=eyJpIjoiZGM1ZTgxNzhiM2E4NDE3M2E1NDhlZDQxMjgxYTliYjAiLCJwIjoiaiJ9
                 * TODO: Make this dynamic to year of election
                 *  */
                const toggleElectionId = electionId.replace(
                    /-election-2025/g,
                    '',
                )
                const isPrefixEnabled = isFeatureEnabled(
                    toFeatureState(store.getState().toggles),
                    `${toggleElectionId}-election-live-title-prefix` as Features,
                )

                const routeType = resolution.resolution.routeType
                const electionDefinition = getLoadedElectionDefinition(
                    electionId,
                    resolution.resolution.electionData,
                    resolution.resolution.meta,
                    isPrefixEnabled,
                )

                switch (routeType) {
                    case 'landing-page':
                        return createSpecificElectionLandingPage(
                            resolution,
                            electionDefinition,
                            getAdTargeting,
                            store,
                            config,
                        )
                    case 'electorates-page':
                        return createSpecificElectoratesPage(
                            electionDefinition,
                            getAdTargeting,
                            store,
                        )
                    case 'party-totals-page':
                        return createSpecificPartyTotalsPage(
                            electionDefinition,
                            getAdTargeting,
                            store,
                        )
                    case 'embed-page':
                        return createSpecificElectionEmbedPage(
                            electionDefinition,
                            getAdTargeting,
                            store,
                        )
                    case 'pollie-rater-page':
                        return createSpecificPollieRaterPage(
                            electionDefinition,
                            getAdTargeting,
                            store,
                        )
                    case 'map-page':
                        return createSpecificElectionMapPage(
                            electionDefinition,
                            getAdTargeting,
                            store,
                        )
                    default:
                        return null
                }
            },
            profile: ({
                config,
                getAdTargeting,
                resolution,
                location,
                store,
            }) => {
                return getProfilePageRouteInfo({
                    profile: resolution.resolution.profile,
                    section: resolution.resolution.section as TheWestSection,
                    config,
                    getAdTargeting,
                    location,
                    store,
                })
            },
            redirect: ({ resolution }) => {
                return getRedirectRouteInfo(
                    resolution.resolution.redirectTo.targetUrl,
                )
            },
            topic: ({ resolution, config, getAdTargeting }) => {
                return getTopicPageRouteInfo({
                    publicationKind: resolution.resolution.publicationKind,
                    topic: resolution.resolution.topic,
                    config,
                    section: resolution.resolution.section as TheWestSection,
                    getAdTargeting,
                    classification: resolution.resolution.classification,
                })
            },
            publication: ({
                resolution,
                config,
                store,
                getAdTargeting,
                renderTarget,
            }) => {
                const toggles = store.getState().toggles
                const regionalMeta = isFeatureEnabled(
                    toFeatureState(toggles),
                    'regionals',
                    false,
                )
                    ? regionalCanonicalMeta(
                          config,
                          store.getState().meta.sectionMeta,
                          resolution.resolution.publication,
                      )
                    : undefined
                const sectionMeta = getSectionMetaInfo(
                    store.getState().meta.sectionMeta,
                    resolution.resolution.section as TheWestSection,
                )

                if (
                    resolution.resolution.publication.kind === 'redirect' &&
                    resolution.resolution.publication.redirectUrl
                ) {
                    return getRedirectRouteInfo(
                        resolution.resolution.publication.redirectUrl,
                    )
                }
                if (
                    isArticleLikePublication(resolution.resolution.publication)
                ) {
                    const shouldResolveFeatureRoute = isFeatureEnabled(
                        toFeatureState(toggles),
                        'immersive-story-article-template',
                    )
                    const shouldResolveShorthandRoute = isFeatureEnabled(
                        toFeatureState(toggles),
                        'shorthand-article-enabled',
                    )

                    if (
                        resolution.resolution.publication.layoutView ===
                            'feature' &&
                        shouldResolveFeatureRoute
                    ) {
                        return createFeatureRoute({
                            config,
                            article: resolution.resolution.publication,
                            section: resolution.resolution
                                .section as TheWestSection,
                            getAdTargeting,
                            sectionMeta,
                            pageMeta: regionalMeta,
                            toggles,
                        })
                    }

                    if (
                        resolution.resolution.publication.layoutView ===
                            'shorthand' &&
                        shouldResolveShorthandRoute
                    ) {
                        return createShorthandRoute({
                            article: resolution.resolution.publication,
                            section: resolution.resolution
                                .section as TheWestSection,
                            getAdTargeting,
                            pageMeta: regionalMeta,
                            toggles,
                        })
                    }

                    return createArticleRoute({
                        config,
                        article: resolution.resolution.publication,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                        pageMeta: regionalMeta,
                        renderTarget,
                        toggles,
                    })
                }
                if (resolution.resolution.publication.kind === 'gallery') {
                    return createGalleryRoute({
                        gallery: resolution.resolution.publication,
                        numberGalleryItems:
                            resolution.resolution.publication.imageCount,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                        pageMeta: regionalMeta,
                        toggles,
                    })
                }
                return null
            },
            preview: ({ resolution, config, getAdTargeting, renderTarget }) => {
                if (
                    isArticleLikePublication(resolution.resolution.publication)
                ) {
                    return createArticleRoute({
                        config,
                        article: resolution.resolution.publication,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                        renderTarget,
                    })
                }
                if (resolution.resolution.publication.kind === 'gallery') {
                    return createGalleryRoute({
                        gallery: resolution.resolution.publication,
                        numberGalleryItems:
                            resolution.resolution.publication.imageCount,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                    })
                }

                return null
            },
            'video-series': () => {
                // TODO DPT-1990 implement video series landing page
                return null
            },
            'video-series-episode': () => {
                // TODO DPT-1991 implement video series episode page
                return null
            },
            video: () => {
                return null
            },
            'video-topic': () => {
                return null
            },
            'match-centre': () => {
                return null
            },
            'travel-edition': () => {
                return null
            },
            sitemap: () => {
                return null
            },
            'afl-match-centre': ({
                config,
                getAdTargeting,
                resolution,
                store,
            }) => {
                const { toggles } = store.getState()
                const isMatchCentreEnabled =
                    toggles['the-west-afl-fixture-scoreboard']
                if (!isMatchCentreEnabled) {
                    return null
                }
                return createAflMatchCentreRoute({
                    config,
                    getAdTargeting,
                    meta: resolution.resolution.meta,
                    match: resolution.resolution.match,
                    competition: resolution.resolution.competition,
                    season: resolution.resolution.season,
                    apiData: resolution.resolution.apiData,
                })
            },
            editions: () => {
                return null
            },
        },
        errorRouteInfo: staticSomethingWentWrongRoute,
        notFoundRouteInfo: notFoundRoute,
    })
}

initRouting()
export const App: React.FC<AppProps> = ({
    onEvent,
    hostname,
    protocol,
    services,
    gptApi,
}) => {
    const location = useLocation()
    if (services.config.datadogEnv !== undefined) {
        datadogRum.init({
            applicationId: '07e29683-e3f4-4f17-91de-42713e7e97e1',
            clientToken: 'puba024ca6587603ce980e953d8067d5b76',
            // `site` refers to the Datadog site parameter of your organization
            // see https://docs.datadoghq.com/getting_started/site/
            site: 'datadoghq.com',
            service: 'the-west',
            env: services.config.datadogEnv,
            // Specify a version number to identify the deployed version of your application in Datadog
            // version: '1.0.0',
            sessionSampleRate: 1,
            sessionReplaySampleRate: 0, // https://wanews.atlassian.net/browse/DPT-1646
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',

            /**
             * disable tracing: we're seeing it produce errors:
             * ```
             * Failed to load resource: Request header field traceparent
             * is not allowed by Access-Control-Allow-Headers.
             * ```
             * */
            allowedTracingUrls: [],
        })
    }
    const { renderTarget } = React.useContext(RenderTargetContext)
    return (
        <PageResolver
            layout={layout}
            hostname={hostname}
            protocol={protocol}
            location={location}
            onEvent={onEvent}
            siteAds={siteAds}
            services={services}
            renderTarget={renderTarget}
            gptApi={gptApi}
            renderPage={(
                pageContents,
                ads,
                pageType,
                section,
                additionalPageProperties,
            ) => {
                const isCountrymanInApp =
                    renderTarget === 'app' && section === 'countryman'
                const resolvedSection = isCountrymanInApp
                    ? 'default'
                    : (section as TheWestSection)
                return (
                    <ProvideRouteResolvedDataToTheme
                        product={Product.TheWest}
                        section={resolvedSection}
                    >
                        {/**
                         * These page properties, are used to represent the state of the
                         * design of the current iteration of the website, and should stay for data integrity
                         * until such a point in time arises where the margins or headline style is altered
                         */}
                        <PageProps
                            pageProperties={{
                                marginSize: 'smaller-margins',
                                headlineStyle: 'serif-headlines',
                            }}
                        />
                        <AuthenticationHandler
                            log={services.log}
                            config={services.config}
                            onEvent={onEvent}
                        />
                        <Site
                            pageType={pageType}
                            section={resolvedSection}
                            additionalPageProperties={additionalPageProperties}
                            config={services.config}
                            onEvent={onEvent}
                            pageContents={pageContents}
                            location={location}
                            ads={ads}
                            hostname={hostname || ''}
                        />
                    </ProvideRouteResolvedDataToTheme>
                )
            }}
        />
    )
}
